import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Chip, TextField, InputAdornment,
  IconButton, Paper,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import config from '../../config';
import { color } from '../../theme/tokens';
import { logAnalyticsEvent } from '../../utils/analytics';

/**
 * Stack-generation CTA for research article pages.
 *
 * Mirrors Detail.js StackCtaCard exactly:
 *  - Fetches real health-goal chips from /supplement-goals/<key>
 *  - Falls back to freeform input (same as detail page)
 *  - For condition pages, surfaces the condition name as the chip
 */
export default function ResearchStackCta({ supplementKey, conditionKey }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const [goalSlugs, setGoalSlugs] = useState([]);
  const [goalsLoaded, setGoalsLoaded] = useState(false);
  const [freeformGoal, setFreeformGoal] = useState('');
  const [inputError, setInputError] = useState(false);

  const source = 'research_article';

  // Format goal slug → human label (mirrors Detail.js)
  const formatGoalLabel = (slug) => config.PRESET_HEALTH_GOALS?.[slug] || slug;

  // Fetch goal chips only for supplement pages
  useEffect(() => {
    if (!supplementKey) { setGoalsLoaded(true); return; }
    fetch(`${config.BASE_URL}/supplement-goals/${encodeURIComponent(supplementKey)}`)
      .then(r => r.ok ? r.json() : { goals: [] })
      .then(data => {
        const unique = Array.from(new Set((data.goals || []).filter(Boolean)));
        unique.sort();
        setGoalSlugs(unique.slice(0, 5));
      })
      .catch(() => setGoalSlugs([]))
      .finally(() => setGoalsLoaded(true));
  }, [supplementKey]);

  useEffect(() => {
    if (!goalsLoaded) return;
    logAnalyticsEvent('research_generate_cta_shown', {
      source,
      supplement_key: supplementKey,
      condition_key: conditionKey,
      chip_count: goalSlugs.length,
    });
  }, [goalsLoaded, source, supplementKey, conditionKey, goalSlugs.length]);

  const navigateToStack = (goal) => {
    logAnalyticsEvent('research_generate_cta_clicked', {
      source,
      supplement_key: supplementKey,
      condition_key: conditionKey,
      health_goal: goal,
    });
    navigate(`/create-stack?healthGoals=${encodeURIComponent(goal)}&auto_generate=true&source=${source}`);
  };

  const handleChipClick = (slug) => navigateToStack(formatGoalLabel(slug));

  const handleConditionChipClick = () => {
    const conditionLabel = conditionKey
      ? conditionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : '';
    navigateToStack(conditionLabel);
  };

  const handleFreeformSubmit = (e) => {
    if (e) e.preventDefault();
    const goal = freeformGoal.trim();
    if (!goal) { setInputError(true); return; }
    setInputError(false);
    navigateToStack(goal);
  };

  const hasChips = goalSlugs.length > 0;
  const isConditionPage = !supplementKey && !!conditionKey;
  const conditionLabel = conditionKey
    ? conditionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : '';

  const chipSx = {
    height: '2.25rem',
    px: 0.25,
    borderRadius: '999rem',
    bgcolor: alpha(theme.palette.primary.light, 0.08),
    color: theme.palette.primary.dark,
    fontWeight: 700,
    fontSize: '0.875rem',
    border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.24)}`,
    transition: 'all 0.16s ease',
    '& .MuiChip-label': { px: 1.2 },
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.light, 0.14),
      borderColor: alpha(theme.palette.primary.light, 0.38),
      color: theme.palette.primary.light,
      transform: 'translateY(-0.0625rem)',
    },
  };

  const freeformInput = (
    <Box component="form" onSubmit={handleFreeformSubmit} sx={{ width: '100%', maxWidth: { sm: '34rem' } }}>
      <Typography variant="caption" sx={{
        display: 'block',
        color: alpha(theme.palette.text.primary, 0.58),
        mb: 0.75,
        fontWeight: 700,
        letterSpacing: '0.01em',
      }}>
        {hasChips || isConditionPage ? 'Or enter your own goal' : 'Enter a health goal'}
      </Typography>
      <TextField
        fullWidth
        placeholder="e.g. Better sleep, Stress relief"
        value={freeformGoal}
        onChange={e => { setFreeformGoal(e.target.value); if (inputError) setInputError(false); }}
        variant="outlined"
        size="small"
        error={inputError}
        helperText={inputError ? 'Please enter a health goal first' : ''}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="submit"
                aria-label="Create My Stack"
                edge="end"
                sx={{
                  width: '2rem', height: '2rem', mr: -0.35,
                  color: color.paper,
                  bgcolor: theme.palette.primary.main,
                  '&:hover': { bgcolor: theme.palette.primary.dark },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: '1.15rem' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: color.paper,
            borderRadius: '0.75rem',
            '& fieldset': { borderColor: color.line },
            '&:hover fieldset': { borderColor: alpha(theme.palette.primary.light, 0.4) },
            '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
            '& input': { py: 1.15 },
          },
        }}
      />
    </Box>
  );

  // Render the card shell (header + freeform input) immediately — do NOT block
  // on the goal-chips fetch. Chips fill in when it resolves (hasChips is false
  // until then). Matches Detail.js StackCtaCard behavior.
  return (
    <Paper elevation={0} sx={{
      borderRadius: '1.25rem',
      border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.22)}`,
      boxShadow: `0 0.25rem 1rem ${alpha(theme.palette.primary.light, 0.1)}`,
      overflow: 'hidden',
      mb: { xs: '1rem', sm: '1.25rem' },
    }}>
      {/* Header */}
      <Box sx={{
        px: { xs: 2.25, sm: 2.75 }, pt: { xs: 2, sm: 2.25 }, pb: 1.75,
        borderBottom: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.1)}`,
      }}>
        {/* Drawn exactly as the supplement and condition pages draw it
            (`Detail.js` StackCtaCard): same variant, weight and size, and solid
            evergreen rather than the sage-to-olive gradient — neither of whose
            colours is in the brief. "AI" is gone too; the product is myStack
            (Tanu, 2026-09-14 — STACK-541). */}
        <Typography variant="h6" sx={{
          fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          letterSpacing: '0.00625rem',
          color: color.evergreen,
          mb: 0.4,
        }}>myStack</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          Start with a health goal to generate a personalized stack.
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{
        px: { xs: 2.25, sm: 2.75 },
        pt: { xs: 1.75, sm: 2 },
        pb: { xs: 2, sm: 2.25 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.5, sm: 1.75 },
      }}>
        {/* Supplement goal chips */}
        {hasChips && (
          <Box>
            <Typography variant="body2" sx={{
              color: alpha(theme.palette.text.primary, 0.82),
              fontWeight: 700,
              letterSpacing: '-0.01em',
              mb: 0.75,
            }}>
              Create a stack for
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 0.875 } }}>
              {goalSlugs.map(slug => (
                <Chip
                  key={slug}
                  label={(
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2 }}>
                      <Box component="span">{formatGoalLabel(slug)}</Box>
                      <ChevronRightIcon sx={{ fontSize: '1rem', mr: -0.35 }} />
                    </Box>
                  )}
                  clickable
                  size="small"
                  onClick={() => handleChipClick(slug)}
                  aria-label={`Create a stack for ${formatGoalLabel(slug)}`}
                  sx={chipSx}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Condition chip */}
        {isConditionPage && conditionLabel && (
          <Box>
            <Typography variant="body2" sx={{
              color: alpha(theme.palette.text.primary, 0.82),
              fontWeight: 700,
              letterSpacing: '-0.01em',
              mb: 0.75,
            }}>
              Create a stack for
            </Typography>
            <Chip
              label={(
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2 }}>
                  <Box component="span">{conditionLabel}</Box>
                  <ChevronRightIcon sx={{ fontSize: '1rem', mr: -0.35 }} />
                </Box>
              )}
              clickable
              size="small"
              onClick={handleConditionChipClick}
              sx={chipSx}
            />
          </Box>
        )}

        {freeformInput}
      </Box>
    </Paper>
  );
}
